import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import PhoneCallsList from "./PhoneCallsList";
import { API } from "@/App";
import { toast } from "sonner";
import { Send, Bot, ChevronLeft } from "lucide-react";

const CH_ICON = { whatsapp: "🟢", instagram: "📸", widget: "💬", phone: "📞" };

const fmtTime = (iso) => {
  if (!iso) return "";
  // stored in UTC; strings without an offset are treated as UTC, then shown in the viewer's local time
  const s = String(iso);
  const d = new Date(/[Z+]|\-\d{2}:\d{2}$/.test(s.slice(10)) ? s : s + "Z");
  if (isNaN(d)) return "";
  const now = new Date();
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return d.toDateString() === now.toDateString()
    ? hm
    : `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")} ${hm}`;
};

const parseUtc = (iso) => {
  const s = String(iso || "");
  const d = new Date(/[Z+]|\-\d{2}:\d{2}$/.test(s.slice(10)) ? s : s + "Z");
  return isNaN(d) ? null : d;
};

// Day separator label for the dialog list ("Today" / "Yesterday" / "Mon, 13 October")
const dayLabelFor = (iso, t) => {
  const d = parseUtc(iso);
  if (!d) return "";
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  if (d.toDateString() === today) return t("inbox.today");
  if (d.toDateString() === yesterday) return t("inbox.yesterday");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long" });
};

export const InboxPanel = ({ t, onUnread }) => {
  const [convos, setConvos] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [brokenImgs, setBrokenImgs] = useState({});
  const [view, setView] = useState("dialogs");
  const bottomRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inbox/conversations`, { params: { q } });
      setConvos(r.data.conversations || []);
      onUnread?.(r.data.unread_total || 0);
    } catch (e) { /* silent */ }
  }, [q, onUnread]);

  useEffect(() => {
    loadList();
    const id = setInterval(loadList, 10000);
    return () => clearInterval(id);
  }, [loadList]);

  const loadMsgs = useCallback(async (sid) => {
    try {
      const r = await axios.get(`${API}/inbox/conversations/${encodeURIComponent(sid)}/messages`);
      setMsgs(r.data.messages || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!sel) return;
    loadMsgs(sel);
    const id = setInterval(() => loadMsgs(sel), 7000);
    return () => clearInterval(id);
  }, [sel, loadMsgs]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length, sel]);

  const selConvo = (convos || []).find((c) => c.session_id === sel);

  const openDialog = (sid) => {
    setSel(sid);
    setConvos((cs) => (cs || []).map((c) => (c.session_id === sid ? { ...c, unread: false } : c)));
  };

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/inbox/conversations/${encodeURIComponent(sel)}/reply`, { text: text.trim() });
      setText("");
      await loadMsgs(sel);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const resumeAi = async () => {
    try {
      await axios.post(`${API}/inbox/conversations/${encodeURIComponent(sel)}/ai-resume`);
      toast.success(t("inbox.aiResumed"));
      loadList();
    } catch (e) { toast.error("Error"); }
  };

  const toggleAi = async (enable) => {
    try {
      await axios.post(`${API}/inbox/conversations/${encodeURIComponent(sel)}/ai-toggle`, { enabled: enable });
      toast.success(enable ? t("inbox.aiEnabledToast") : t("inbox.aiDisabledToast"));
      loadList();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex overflow-hidden" style={{ height: "calc(100vh - 110px)" }} data-testid="inbox-panel">
      {/* Dialog list */}
      <div className={`${sel ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 shrink-0 border-r border-gray-200`}>
        <div className="p-2 border-b border-gray-200">
          <input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("inbox.search")}
            className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
        </div>
        <div className="flex border-b border-gray-200 text-xs font-semibold" data-testid="inbox-view-tabs">
          <button type="button" onClick={() => setView("dialogs")} data-testid="inbox-view-dialogs"
            className={`flex-1 py-2 ${view === "dialogs" ? "border-b-2 border-black" : "text-gray-400"}`}>💬 Диалоги</button>
          <button type="button" onClick={() => setView("calls")} data-testid="inbox-view-calls"
            className={`flex-1 py-2 ${view === "calls" ? "border-b-2 border-black" : "text-gray-400"}`}>📞 Звонки</button>
        </div>
        {view === "calls" && <PhoneCallsList onOpen={(sid) => { setSel(sid); }} />}
        <div className={`flex-1 overflow-y-auto ${view === "calls" ? "hidden" : ""}`} data-testid="inbox-list">
          {convos && !convos.length && <div className="p-4 text-sm text-gray-400">{t("inbox.empty")}</div>}
          {(convos || []).map((c, idx) => {
            const day = parseUtc(c.last_at)?.toDateString();
            const prevDay = idx > 0 ? parseUtc(convos[idx - 1].last_at)?.toDateString() : null;
            return (
            <div key={c.session_id}>
            {day && day !== prevDay && (
              <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-white sticky top-0" data-testid={`inbox-day-${day}`}>{dayLabelFor(c.last_at, t)}</div>
            )}
            <button data-testid={`inbox-convo-${c.session_id}`}
              onClick={() => openDialog(c.session_id)}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors ${sel === c.session_id ? "bg-gray-100" : ""}`}>
              <div className="flex items-center gap-1.5">
                <span className="text-sm">{CH_ICON[c.channel]}</span>
                <span className="font-semibold text-sm truncate">{c.name || c.phone || t("inbox.client")}</span>
                {c.ai_disabled
                  ? <Bot className="w-3.5 h-3.5 text-gray-400 shrink-0" title={t("inbox.aiOff")} />
                  : c.ai_paused && <Bot className="w-3.5 h-3.5 text-orange-500 shrink-0" title={t("inbox.aiPaused")} />}
                <span className="ml-auto text-[10px] text-gray-400 whitespace-nowrap">{fmtTime(c.last_at)}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-xs truncate ${c.unread ? "font-semibold text-gray-900" : "text-gray-500"}`}>
                  {c.last_role === "assistant" ? "↩ " : ""}{c.last_message}
                </span>
                {c.unread && <span className="ml-auto w-2 h-2 rounded-full bg-red-500 shrink-0" data-testid={`inbox-unread-dot-${c.session_id}`} />}
              </div>
            </button>
            </div>
            );
          })}
        </div>
      </div>

      {/* Thread */}
      <div className={`${sel ? "flex" : "hidden md:flex"} flex-col flex-1 min-w-0`}>
        {!sel && <div className="flex-1 flex items-center justify-center text-sm text-gray-400" data-testid="inbox-placeholder">{t("inbox.pickDialog")}</div>}
        {sel && (
          <>
            <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
              <button className="md:hidden p-1 -ml-1" onClick={() => setSel(null)} data-testid="inbox-back-btn"><ChevronLeft className="w-5 h-5" /></button>
              <span>{CH_ICON[selConvo?.channel]}</span>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate" data-testid="inbox-thread-title">{selConvo?.name || selConvo?.phone || t("inbox.client")}</div>
                {selConvo?.phone && selConvo?.name && <div className="text-[11px] text-gray-400">{selConvo.phone}</div>}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {selConvo?.ai_paused && !selConvo?.ai_disabled && (
                  <button data-testid="inbox-resume-ai-btn" onClick={resumeAi}
                    className="flex items-center gap-1.5 text-xs font-medium bg-orange-50 border border-orange-300 text-orange-700 rounded-full px-3 py-1 hover:bg-orange-100 transition-colors">
                    <Bot className="w-3.5 h-3.5" />{t("inbox.resumeAi")}
                  </button>
                )}
                <button data-testid="inbox-ai-toggle" onClick={() => toggleAi(!!selConvo?.ai_disabled)}
                  title={selConvo?.ai_disabled ? t("inbox.aiOff") : t("inbox.aiOn")}
                  className={`flex items-center gap-1.5 text-xs font-medium rounded-full pl-2.5 pr-1.5 py-1 border transition-colors ${selConvo?.ai_disabled ? "bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200" : "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"}`}>
                  <Bot className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{selConvo?.ai_disabled ? t("inbox.aiOff") : t("inbox.aiOn")}</span>
                  <span className={`relative inline-flex w-7 h-4 rounded-full transition-colors ${selConvo?.ai_disabled ? "bg-gray-300" : "bg-green-500"}`}>
                    <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${selConvo?.ai_disabled ? "left-0.5" : "left-3.5"}`} />
                  </span>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50" data-testid="inbox-thread">
              {msgs.map((m, i) => {
                const mine = m.role === "assistant";
                const manual = m.source === "owner_manual";
                return (
                  <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${mine ? (manual ? "bg-gray-900 text-white" : "bg-indigo-600 text-white") : "bg-white border border-gray-200 text-gray-900"}`}>
                      {mine && <div className={`text-[10px] mb-0.5 ${manual ? "text-gray-300" : "text-indigo-200"}`}>{manual ? t("inbox.you") : "AI"}</div>}
                      {m.media_url && m.media_type === "image" && (
                        brokenImgs[`${sel}-${i}`] ? (
                          <div className="text-xs opacity-70 mb-1" data-testid={`inbox-media-expired-${i}`}>📷 Фото (ссылка истекла)</div>
                        ) : (
                          <a href={m.media_url} target="_blank" rel="noreferrer" data-testid={`inbox-media-img-${i}`}>
                            <img src={m.media_url} alt="" loading="lazy"
                              className="rounded-lg max-h-44 max-w-full object-cover mb-1 border border-black/10"
                              onError={() => setBrokenImgs((b) => ({ ...b, [`${sel}-${i}`]: true }))} />
                          </a>
                        )
                      )}
                      {m.media_url && m.media_type === "audio" && (
                        <div className="mb-1" data-testid={`inbox-media-audio-${i}`}>
                          <audio controls preload="none" src={m.media_url} className="h-8 max-w-full" />
                        </div>
                      )}
                      {m.media_url && m.media_type !== "image" && m.media_type !== "audio" && (
                        <a href={m.media_url} target="_blank" rel="noreferrer" data-testid={`inbox-media-link-${i}`}
                          className={`block text-xs underline mb-1 ${mine ? "text-indigo-100" : "text-indigo-700"}`}>
                          {m.media_type === "video" ? "🎥 Видео" : "📎 Файл"} ↗
                        </a>
                      )}
                      {m.transcript ? <span data-testid={`inbox-transcript-${i}`}><span className="opacity-60">🎤 </span><i>«{m.content}»</i></span> : m.content}
                      <div className={`text-[10px] mt-0.5 text-right ${mine ? "opacity-60" : "text-gray-400"}`}>{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            {selConvo?.ai_disabled ? (
              <div className="px-3 py-1.5 bg-gray-100 border-t border-gray-200 text-[11px] text-gray-500 font-medium" data-testid="inbox-disabled-banner">
                🤖 {t("inbox.aiOffBanner")}
              </div>
            ) : selConvo?.ai_paused && (
              <div className="px-3 py-1.5 bg-orange-50 border-t border-orange-200 text-[11px] text-orange-700 font-medium" data-testid="inbox-paused-banner">
                ⏸ {t("inbox.aiPaused")}
              </div>
            )}
            <div className="p-2 border-t border-gray-200 flex gap-2">
              <textarea data-testid="inbox-reply-input" value={text} rows={1}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t("inbox.reply")}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-gray-400" />
              <button data-testid="inbox-send-btn" onClick={send} disabled={busy || !text.trim()}
                className="bg-gray-900 text-white rounded-lg px-4 flex items-center justify-center disabled:opacity-40 hover:bg-gray-800 transition-colors">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
